"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { fetchAuthSession, hasAuthSession, readAuthUserLabel } from "@/lib/auth-client"
import { performClientLogoutCleanup } from "@/lib/client-session-cleanup"
import { api } from "@/lib/api"
import type { UserProfile } from "@/lib/api"
import {AUTH_USER_ID_KEY} from "@/lib/auth-constants";

interface PendingAuth {
  email: string
  password: string
}

interface AuthContextType {
  user: string | null
  userId: string | null
  profile: UserProfile | null
  pendingAuth: PendingAuth | null
  setAuth: (user: string, email?: string, password?: string) => void
  setProfile: (profile: UserProfile) => void
  setPendingAuth: (email: string, password: string) => void
  clearPendingAuth: () => void
  logout: () => Promise<void>
  isAuthenticated: boolean
  isInitialized: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

const PENDING_AUTH_KEY = "pending_auth_session"

function resolveAuthUser(fallback?: string | null): string {
  return readAuthUserLabel() ?? fallback ?? "Account"
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<string | null>(null)
  const [sessionAuthenticated, setSessionAuthenticated] = useState(false)
  const [profile, setProfileState] = useState<UserProfile | null>(null)
  const [userId, setUserId] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem(AUTH_USER_ID_KEY)
    }
    return null
  })
  const [pendingAuth, setPendingAuthState] = useState<PendingAuth | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const savedPendingAuth = sessionStorage.getItem(PENDING_AUTH_KEY)
    if (savedPendingAuth) {
      try {
        setPendingAuthState(JSON.parse(savedPendingAuth))
      } catch {
        sessionStorage.removeItem(PENDING_AUTH_KEY)
      }
    }

    // Hydrate immediately from readable auth cookie (auth_token is httpOnly).
    if (hasAuthSession()) {
      setSessionAuthenticated(true)
      setUser(resolveAuthUser())
    }

    void fetchAuthSession()
      .then((session) => {
        if (session.authenticated) {
          setSessionAuthenticated(true)
          setUser(resolveAuthUser(session.user))
        } else {
          setSessionAuthenticated(false)
          setUser(null)
        }
      })
      .finally(() => setIsInitialized(true))
  }, [])

  useEffect(() => {
    const refreshSession = () => {
      void fetchAuthSession().then((session) => {
        if (session.authenticated) {
          setSessionAuthenticated(true)
          setUser(resolveAuthUser(session.user))
        } else {
          setSessionAuthenticated(false)
          setUser(null)
        }
      })
    }

    const onSessionExpired = () => {
      setSessionAuthenticated(false)
      setUser(null)
    }

    window.addEventListener("auth:session-changed", refreshSession)
    window.addEventListener("auth:session-expired", onSessionExpired)
    return () => {
      window.removeEventListener("auth:session-changed", refreshSession)
      window.removeEventListener("auth:session-expired", onSessionExpired)
    }
  }, [])

  const setAuth = useCallback((newUser: string, email?: string, password?: string) => {
    setSessionAuthenticated(true)
    setUser(newUser)
    if (typeof window !== "undefined") {
      // Drop any leftover anonymous compiler session/state — the user is now authenticated
      // and should always get their own auth-scoped, per-project session instead.
      localStorage.removeItem("rudrise-session-id-v1")
      localStorage.removeItem("rudrise-compiler-state-v1")
      window.dispatchEvent(new CustomEvent("auth:session-changed"))
    }

    if (email && password && typeof window !== "undefined") {
      const pending = { email, password }
      sessionStorage.setItem(PENDING_AUTH_KEY, JSON.stringify(pending))
      setPendingAuthState(pending)
    }
  }, [])

  const setPendingAuth = useCallback((email: string, password: string) => {
    const pending = { email, password }
    setPendingAuthState(pending)
    if (typeof window !== "undefined") {
      sessionStorage.setItem(PENDING_AUTH_KEY, JSON.stringify(pending))
    }
  }, [])

  const clearPendingAuth = useCallback(() => {
    setPendingAuthState(null)
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(PENDING_AUTH_KEY)
    }
  }, [])

  const setProfile = useCallback((p: UserProfile) => {
    setProfileState(p)
    if (p?.id) {
      setUserId(p.id)
      if (typeof window !== "undefined") {
        sessionStorage.setItem(AUTH_USER_ID_KEY, p.id)
      }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // Cookie may already be cleared — still reset client state.
    }

    await performClientLogoutCleanup()

    setSessionAuthenticated(false)
    setUser(null)
    setUserId(null)
    setProfileState(null)
    setPendingAuthState(null)

    if (typeof window !== "undefined") {
      sessionStorage.removeItem(AUTH_USER_ID_KEY)
      window.dispatchEvent(new CustomEvent("auth:session-changed"))
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        userId: userId ?? profile?.id ?? null,
        profile,
        pendingAuth,
        setAuth,
        setProfile,
        setPendingAuth,
        clearPendingAuth,
        logout,
        isAuthenticated: sessionAuthenticated,
        isInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
